import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input, QRCode, Skeleton, Typography } from "antd";
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
} from "aws-amplify/auth";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getENV } from "../../stores/consts";
import { useMst } from "../../stores/main";
import { BlingsButton } from "../BlingsButton/BlingsButton";
import "./withSetupTOTPMFA.scss";

const { Title, Text, Paragraph } = Typography;

const withSetupTOTPMFA = (WrappedComponent: React.ComponentType<any>) => {
  return (props: any) => {
    const { accountStore } = useMst();
    const [needToSetTOTP, setNeedToSetTOTP] = useState<boolean>(false);

    const [QRCodeValue, setQRCodeValue] = useState<string | null>(null);
    const [confirmationCount, setConfirmationCount] = useState<number>(0);
    const [token, setToken] = useState<string>("");

    const isMountedRef = useRef(true);
    const callbackRef = useRef<(() => void) | null>(null);

    const setupTOTP = useCallback(async () => {
      try {
        const userEmail = accountStore.userAttributes?.email;
        const appName = getENV() === "master" ? "Blings" : "Blings: dev";
        const totpSetupDetails = await setUpTOTP();
        const setupUri = totpSetupDetails.getSetupUri(appName, userEmail);
        if (isMountedRef.current) {
          setQRCodeValue(setupUri.toString());
        }
      } catch (err) {
        console.error(err);
      }
    }, [accountStore]);

    const checkMFA = useCallback(async () => {
      const callback = () => {
        if (!isMountedRef.current) return;
        const mfaEnabled = accountStore.MFAEnabled;
        const mfaRequired = accountStore.MFARequired;
        if (mfaRequired && !mfaEnabled) {
          setNeedToSetTOTP(true);
          setupTOTP();
        }
      };
      callbackRef.current = callback;
      accountStore.addFunctionToLoadCallback(callback);
    }, [accountStore, setupTOTP]);

    const verifyTOTP = async (token: string) => {
      try {
        await verifyTOTPSetup({ code: token });

        if (!isMountedRef.current) return;

        setConfirmationCount((prevCount) => prevCount + 1);
        if (confirmationCount + 1 >= 2) {
          await updateMFAPreference({ totp: "PREFERRED" });
          if (!isMountedRef.current) return;
          setNeedToSetTOTP(false);
          setQRCodeValue(null);
          setToken("");
          toast.success("MFA auth enabled!");
        } else {
          toast.success(
            "First confirmation successful! Please confirm once more."
          );
        }
      } catch (_err) {
        const err = (_err as Error).toString();
        console.error(err);
        if (err.includes("InvalidParameterException")) {
          toast.error("Invalid token. The maximum length is 6.");
        } else if (err.includes("Code mismatch")) {
          toast.error("Invalid token. Please try again.");
        } else if (err.includes("EnableSoftwareTokenMFAException")) {
          toast.error("Invalid token. This token has already been used once.");
        }
      }
    };

    useEffect(() => {
      isMountedRef.current = true;
      checkMFA();
      return () => {
        isMountedRef.current = false;
        if (callbackRef.current) {
          accountStore.removeFunctionFromLoadCallback(callbackRef.current);
        }
      };
    }, [checkMFA, accountStore]);

    useEffect(() => {
      if (needToSetTOTP) setupTOTP();
    }, [needToSetTOTP, setupTOTP]);

    if (!needToSetTOTP) {
      return <WrappedComponent {...props} />;
    }

    return (
      <div className="MfaSetup">
        <ToastContainer
          position="bottom-left"
          autoClose={2500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <div className="MfaSetup__panel">
          <div className="MfaSetup__header">
            <Title level={3}>Set up your authenticator</Title>
            <Paragraph className="MfaSetup__subtitle">
              <Text>
                Your account requires authenticator-based MFA. Scan the QR code
                with your app and enter the 6-digit code. We will ask for the
                code twice to confirm the setup.
              </Text>
            </Paragraph>
          </div>
          <div className="MfaSetup__body">
            <div className="MfaSetup__steps">
              <div className="MfaSetup__step">
                <div className="MfaSetup__stepBadge">1</div>
                <div>
                  <Text strong>Open your authenticator app</Text>
                  <Paragraph className="MfaSetup__stepText">
                    We recommend Google Authenticator, Authy, 1Password, or
                    Bitwarden.
                  </Paragraph>
                </div>
              </div>
              <div className="MfaSetup__step">
                <div className="MfaSetup__stepBadge">2</div>
                <div>
                  <Text strong>Scan the QR code</Text>
                  <Paragraph className="MfaSetup__stepText">
                    Point your camera at the code to add your account.
                  </Paragraph>
                </div>
              </div>
              <div className="MfaSetup__step">
                <div className="MfaSetup__stepBadge">3</div>
                <div>
                  <Text strong>Enter the 6-digit code</Text>
                  <Paragraph className="MfaSetup__stepText">
                    This confirms the authenticator is linked to your account.
                  </Paragraph>
                </div>
              </div>
              <div className="MfaSetup__step">
                <div className="MfaSetup__stepBadge">4</div>
                <div>
                  <Text strong>Confirm the next code</Text>
                  <Paragraph className="MfaSetup__stepText">
                    Enter the next 6-digit code to finalize setup.
                  </Paragraph>
                </div>
              </div>
            </div>
            <div className="MfaSetup__qrCard">
              {QRCodeValue ? (
                <>
                  <QRCode value={QRCodeValue} size={240} />
                  <div className="MfaSetup__formRow">
                    <Input
                      id="totpToken"
                      placeholder="Enter 6-digit code"
                      inputMode="numeric"
                      maxLength={6}
                      value={token}
                      onChange={(event) => setToken(event.target.value)}
                      onPressEnter={() => verifyTOTP(token)}
                      className="MfaSetup__input"
                    />
                    <BlingsButton
                      className="MfaSetup__button"
                      disabled={token.trim().length !== 6}
                      onClick={() => verifyTOTP(token)}
                    >
                      Confirm TOTP Token
                    </BlingsButton>
                  </div>
                  <Text className="MfaSetup__note">
                    You will be prompted to enter the next 6-digit code once the
                    first confirmation succeeds.
                  </Text>
                </>
              ) : (
                <Skeleton.Image active={QRCodeValue !== undefined} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
};

export default withSetupTOTPMFA;
